/**
 * BSV Transaction Builder Module
 *
 * This module provides client-side transaction-building helpers and console
 * testing utilities for the self-custody wallet code. It is currently exposed
 * via `window.bsvTxBuilder` from `wallets.ts`.
 *
 * Important: the active local-signing upvalue flow no longer builds
 * transactions here. Upvalue signing now uses the exact server-prepared
 * unsigned template from `client-signing-flow.ts`, then submits the signed
 * transaction back to the backend for validation and broadcast.
 */

import { P2PKH } from '@bsv/sdk'
import {
    signTransaction,
    getClientShare,
    recoverPaymentKeyWifWithShares,
    type TransactionInputSpec,
    type UnsignedTransactionTemplate,
} from './bsv-signing'
import api from 'api/api'

// ============================================================================
// Configuration
// ============================================================================

/** Fallback fee rate if API fails */
const FALLBACK_FEE_RATE_SAT_PER_BYTE = 1

/** Cached fee rate and its expiry time */
let cachedFeeRate: { rate: number; expiry: number } | null = null

/** Dust threshold - outputs below this are unspendable */
const DUST_THRESHOLD = 546

/** Maximum retry attempts on UTXO conflicts */
const MAX_RETRY_ATTEMPTS = 3

/** Delay between retries in milliseconds */
const RETRY_DELAY_MS = 500

// ============================================================================
// Types
// ============================================================================

/**
 * UTXO as returned by WhatsOnChain API
 */
export interface WocUtxo {
    /** Transaction hash */
    tx_hash: string
    /** Output position */
    tx_pos: number
    /** Amount in satoshis */
    value: number
    /** Block height (0 if unconfirmed) */
    height: number
}

/**
 * Enhanced UTXO with script information for spending
 */
export interface SpendableUtxo extends WocUtxo {
    /** P2PKH locking script in hex */
    scriptPubKey: string
}

/**
 * Parameters for building a simple payment transaction (single recipient)
 */
export interface PaymentParams {
    /** Recipient BSV address */
    recipientAddress: string
    /** Amount to send in satoshis */
    amount: number
    /** Optional: OP_RETURN data (hex encoded) */
    opReturnData?: string
}

/**
 * Parameters for building an upvalue transaction (author + Treechat fee split)
 */
export interface UpvalueParams {
    /** Author's BSV address (receives 85%) */
    authorAddress: string
    /** Treechat fee address (receives 15%) */
    treechatFeeAddress: string
    /** Total upvalue amount in satoshis (will be split 85/15) */
    totalAmount: number
    /** Optional: OP_RETURN data (hex encoded) */
    opReturnData?: string
}

/**
 * Output specification for multi-output transactions
 */
export interface OutputSpec {
    /** Recipient address */
    address: string
    /** Amount in satoshis */
    satoshis: number
}

/**
 * Result of building a transaction
 */
export interface BuildResult {
    success: boolean
    /** Unsigned transaction template (if success) */
    template?: UnsignedTransactionTemplate
    /** Selected UTXOs (for cache invalidation on retry) */
    selectedUtxos?: SpendableUtxo[]
    /** Total input amount */
    totalInput?: number
    /** Total output amount (including change) */
    totalOutput?: number
    /** Transaction fee */
    fee?: number
    /** Error message (if !success) */
    error?: string
}

/**
 * Result of the full build-sign-broadcast flow
 */
export interface BroadcastResult {
    success: boolean
    /** Transaction ID (if success) */
    txid?: string
    /** Signed raw transaction hex (if success) */
    rawTx?: string
    /** Error message (if !success) */
    error?: string
    /** Whether the error is retryable */
    retryable?: boolean
}

// ============================================================================
// Fee Rate
// ============================================================================

/**
 * Fetches the current fee rate from the backend.
 * The backend caches this for 15 minutes and applies a 10% safety margin.
 *
 * @returns Fee rate in satoshis per byte
 */
export async function fetchFeeRate(): Promise<number> {
    // Check client-side cache first (5 minute TTL)
    if (cachedFeeRate && Date.now() < cachedFeeRate.expiry) {
        return cachedFeeRate.rate
    }

    try {
        const result = await api.getBsvFeeRate()
        const rate = result.fee_rate ?? FALLBACK_FEE_RATE_SAT_PER_BYTE

        // Cache for 5 minutes client-side (backend caches for 15 minutes)
        cachedFeeRate = {
            rate,
            expiry: Date.now() + 5 * 60 * 1000,
        }

        console.log(`>>> Fee rate: ${rate} sat/byte`)
        return rate
    } catch (error) {
        console.warn('>>> Failed to fetch fee rate, using fallback:', error)
        return FALLBACK_FEE_RATE_SAT_PER_BYTE
    }
}

// ============================================================================
// UTXO Fetching
// ============================================================================

/**
 * Fetches UTXOs for the current user via Treechat's backend BSV service.
 *
 * @param address - BSV address to fetch UTXOs for
 * @returns Array of spendable UTXOs (with scriptPubKey included)
 * @throws Error if the backend request fails
 */
export async function fetchUtxos(address: string): Promise<SpendableUtxo[]> {
    const utxos = await api.getBsvUtxos()
    const scriptPubKey = generateP2PKHScript(address)

    return utxos.map(utxo => ({
        tx_hash: utxo.tx_hash,
        tx_pos: utxo.tx_pos,
        value: utxo.value,
        height: utxo.height,
        scriptPubKey,
    }))
}

/**
 * Generates a P2PKH locking script for an address.
 *
 * @param address - BSV address
 * @returns Locking script in hex format
 */
export function generateP2PKHScript(address: string): string {
    const p2pkh = new P2PKH()
    const lockingScript = p2pkh.lock(address)
    return lockingScript.toHex()
}

// ============================================================================
// UTXO Selection
// ============================================================================

/**
 * Selects UTXOs to cover the required amount using largest-first strategy.
 *
 * This simple greedy algorithm minimizes the number of inputs, which reduces
 * transaction size and fees. It's not optimal for privacy but is efficient
 * and predictable.
 *
 * @param utxos - Available UTXOs
 * @param requiredAmount - Total amount needed (including fees)
 * @returns Selected UTXOs and total value, or null if insufficient funds
 */
export function selectUtxos(
    utxos: SpendableUtxo[],
    requiredAmount: number,
): { selected: SpendableUtxo[]; total: number } | null {
    // Sort by value descending (largest first)
    const sorted = [...utxos].sort((a, b) => b.value - a.value)

    const selected: SpendableUtxo[] = []
    let total = 0

    for (const utxo of sorted) {
        selected.push(utxo)
        total += utxo.value

        if (total >= requiredAmount) {
            return { selected, total }
        }
    }

    // Insufficient funds
    return null
}

// ============================================================================
// Fee Estimation
// ============================================================================

/**
 * Estimates transaction size in bytes.
 *
 * P2PKH transaction size breakdown:
 * - Base: 10 bytes (version + locktime + input/output counts)
 * - Per input: ~148 bytes (outpoint: 36, scriptSig: ~107, sequence: 4)
 * - Per output: 34 bytes (value: 8, scriptPubKey: 25, length: 1)
 * - OP_RETURN: variable (8 + script length)
 *
 * @param inputCount - Number of inputs
 * @param outputCount - Number of outputs (not including OP_RETURN)
 * @param opReturnSize - Size of OP_RETURN data in bytes (0 if none)
 * @returns Estimated transaction size in bytes
 */
export function estimateTxSize(
    inputCount: number,
    outputCount: number,
    opReturnSize: number = 0,
): number {
    // Match Node.js constants from SHUAllet.js:
    // P2PKH_SIGSCRIPT_SIZE = 1 + 73 + 1 + 33 = 108
    // P2PKH_INPUT_SIZE = 36 + 1 + 108 + 4 = 149
    // P2PKH_OUTPUT_SIZE = 8 + 1 + 1 + 1 + 1 + 20 + 1 + 1 = 34
    const baseSize = 10
    const inputSize = 149 * inputCount
    const outputSize = 34 * outputCount
    const opReturnOverhead = opReturnSize > 0 ? 8 + opReturnSize : 0

    return baseSize + inputSize + outputSize + opReturnOverhead
}

/**
 * Calculates the transaction fee.
 *
 * @param txSizeBytes - Transaction size in bytes
 * @param feeRate - Fee rate in satoshis per byte (default: 1)
 * @returns Fee in satoshis
 */
export function calculateFee(
    txSizeBytes: number,
    feeRate: number = FALLBACK_FEE_RATE_SAT_PER_BYTE,
): number {
    return Math.ceil(txSizeBytes * feeRate)
}

// ============================================================================
// Transaction Building
// ============================================================================

/** Treechat fee percentage for upvalues (15%) */
const TREECHAT_FEE_PERCENTAGE = 0.15

/**
 * Builds an OP_RETURN script from hex data.
 */
function buildOpReturnScript(dataHex: string): string {
    const dataLen = dataHex.length / 2

    if (dataLen <= 75) {
        // Direct push
        const pushOp = dataLen.toString(16).padStart(2, '0')
        return '006a' + pushOp + dataHex
    } else if (dataLen <= 255) {
        // OP_PUSHDATA1
        const lenHex = dataLen.toString(16).padStart(2, '0')
        return '006a4c' + lenHex + dataHex
    } else {
        // OP_PUSHDATA2
        const lenHex = dataLen.toString(16).padStart(4, '0')
        // Little-endian
        const lenLE = lenHex.slice(2, 4) + lenHex.slice(0, 2)
        return '006a4d' + lenLE + dataHex
    }
}

/**
 * Pure transaction builder that takes UTXOs directly.
 * This is the core building logic, testable without network calls.
 *
 * @param utxos - Available UTXOs to spend from
 * @param changeAddress - Address to send change to
 * @param requestedOutputs - Array of outputs to include (excluding change)
 * @param opReturnData - Optional OP_RETURN data in hex
 * @param feeRate - Fee rate in satoshis per byte (fetch with fetchFeeRate())
 * @returns BuildResult with the unsigned transaction template
 */
export function assembleTransaction(
    utxos: SpendableUtxo[],
    changeAddress: string,
    requestedOutputs: OutputSpec[],
    opReturnData?: string,
    feeRate: number = FALLBACK_FEE_RATE_SAT_PER_BYTE,
): BuildResult {
    try {
        // Validate we have UTXOs
        if (utxos.length === 0) {
            return {
                success: false,
                error: 'No UTXOs provided',
            }
        }

        // Calculate total output amount (excluding change)
        const totalOutputAmount = requestedOutputs.reduce((sum, o) => sum + o.satoshis, 0)

        // Count outputs: requested outputs + change (assume we'll have change)
        let outputCount = requestedOutputs.length + 1 // +1 for change

        // OP_RETURN size
        const opReturnSize = opReturnData ? opReturnData.length / 2 : 0

        // First pass: estimate with 1 input to start
        let estimatedInputCount = 1
        let estimatedSize = estimateTxSize(estimatedInputCount, outputCount, opReturnSize)
        let estimatedFee = calculateFee(estimatedSize, feeRate)

        // Select UTXOs
        let totalRequired = totalOutputAmount + estimatedFee
        let selection = selectUtxos(utxos, totalRequired)

        // Iterate if we need more inputs (which increases fee)
        let iterations = 0
        const maxIterations = 10

        while (
            selection &&
            selection.selected.length > estimatedInputCount &&
            iterations < maxIterations
        ) {
            estimatedInputCount = selection.selected.length
            estimatedSize = estimateTxSize(estimatedInputCount, outputCount, opReturnSize)
            estimatedFee = calculateFee(estimatedSize, feeRate)
            totalRequired = totalOutputAmount + estimatedFee
            selection = selectUtxos(utxos, totalRequired)
            iterations++
        }

        if (!selection) {
            const totalAvailable = utxos.reduce((sum, u) => sum + u.value, 0)
            return {
                success: false,
                error: `Insufficient funds: need ${totalRequired} sats, have ${totalAvailable} sats`,
            }
        }

        // Calculate change
        let change = selection.total - totalOutputAmount - estimatedFee

        // Re-estimate without change output if change is dust
        if (change < DUST_THRESHOLD && change > 0) {
            // Remove change output, add to fee
            outputCount--
            estimatedSize = estimateTxSize(selection.selected.length, outputCount, opReturnSize)
            estimatedFee = calculateFee(estimatedSize, feeRate)

            // The "change" becomes part of the fee
            estimatedFee = selection.total - totalOutputAmount
            change = 0
        }

        // Build outputs
        const outputs: { script: string; satoshis: number }[] = []

        // Add requested outputs
        for (const output of requestedOutputs) {
            outputs.push({
                script: generateP2PKHScript(output.address),
                satoshis: output.satoshis,
            })
        }

        // Add change output (if above dust)
        if (change >= DUST_THRESHOLD) {
            outputs.push({
                script: generateP2PKHScript(changeAddress),
                satoshis: change,
            })
        }

        // Add OP_RETURN output (if applicable)
        if (opReturnData) {
            outputs.push({
                script: buildOpReturnScript(opReturnData),
                satoshis: 0,
            })
        }

        // Build inputs
        const inputs: TransactionInputSpec[] = selection.selected.map(utxo => ({
            txid: utxo.tx_hash,
            vout: utxo.tx_pos,
            satoshis: utxo.value,
            scriptPubKey: utxo.scriptPubKey,
        }))

        // Create template
        const template: UnsignedTransactionTemplate = {
            inputs,
            outputs,
            version: 1,
            lockTime: 0,
        }

        return {
            success: true,
            template,
            selectedUtxos: selection.selected,
            totalInput: selection.total,
            totalOutput: outputs.reduce((sum, o) => sum + o.satoshis, 0),
            fee: estimatedFee,
        }
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        return {
            success: false,
            error: `Failed to build transaction: ${errorMessage}`,
        }
    }
}

/**
 * Fetches UTXOs and builds a transaction. Convenience wrapper around assembleTransaction.
 *
 * @param senderAddress - Address to fetch UTXOs from and send change to
 * @param requestedOutputs - Array of outputs to include (excluding change)
 * @param opReturnData - Optional OP_RETURN data in hex
 * @returns BuildResult with the unsigned transaction template
 */
async function buildTransactionFromOutputs(
    senderAddress: string,
    requestedOutputs: OutputSpec[],
    opReturnData?: string,
): Promise<BuildResult> {
    // Fetch fee rate and UTXOs in parallel
    const [feeRate, utxos] = await Promise.all([fetchFeeRate(), fetchUtxos(senderAddress)])

    if (utxos.length === 0) {
        return {
            success: false,
            error: 'No UTXOs available',
        }
    }

    return assembleTransaction(utxos, senderAddress, requestedOutputs, opReturnData, feeRate)
}

/**
 * Builds an unsigned transaction for a simple payment (single recipient).
 *
 * @param senderAddress - Address sending the payment
 * @param params - Payment parameters
 * @returns BuildResult with the unsigned transaction template
 */
export async function buildPaymentTransaction(
    senderAddress: string,
    params: PaymentParams,
): Promise<BuildResult> {
    const outputs: OutputSpec[] = [
        {
            address: params.recipientAddress,
            satoshis: params.amount,
        },
    ]

    return buildTransactionFromOutputs(senderAddress, outputs, params.opReturnData)
}

/**
 * Builds an unsigned transaction for an upvalue (85% to author, 15% to Treechat).
 *
 * @param senderAddress - Address sending the upvalue
 * @param params - Upvalue parameters
 * @returns BuildResult with the unsigned transaction template
 */
export async function buildUpvalueTransaction(
    senderAddress: string,
    params: UpvalueParams,
): Promise<BuildResult> {
    // Calculate the split (15% to Treechat, 85% to author)
    const treechatAmount = Math.floor(params.totalAmount * TREECHAT_FEE_PERCENTAGE)
    const authorAmount = params.totalAmount - treechatAmount

    // Validate amounts are above dust
    if (authorAmount < DUST_THRESHOLD) {
        return {
            success: false,
            error: `Author amount ${authorAmount} sats is below dust threshold. Minimum upvalue is ${Math.ceil(
                DUST_THRESHOLD / (1 - TREECHAT_FEE_PERCENTAGE),
            )} sats.`,
        }
    }

    if (treechatAmount < DUST_THRESHOLD) {
        return {
            success: false,
            error: `Treechat fee ${treechatAmount} sats is below dust threshold. Minimum upvalue is ${Math.ceil(
                DUST_THRESHOLD / TREECHAT_FEE_PERCENTAGE,
            )} sats.`,
        }
    }

    const outputs: OutputSpec[] = [
        {
            address: params.authorAddress,
            satoshis: authorAmount,
        },
        {
            address: params.treechatFeeAddress,
            satoshis: treechatAmount,
        },
    ]

    return buildTransactionFromOutputs(senderAddress, outputs, params.opReturnData)
}

// Keep buildTransaction as an alias for buildPaymentTransaction for backwards compatibility
export const buildTransaction = buildPaymentTransaction

// ============================================================================
// Signing
// ============================================================================

/**
 * Result of signing a transaction
 */
export interface SignResult {
    success: boolean
    /** Signed transaction in raw hex format */
    rawTx?: string
    /** Error message if signing failed */
    error?: string
}

/**
 * Signs a transaction template with a WIF private key.
 * This is the final step before broadcasting.
 *
 * @param template - Unsigned transaction template from assembleTransaction
 * @param privateKeyWIF - Private key in WIF format
 * @returns SignResult with rawTx hex ready for broadcast
 */
export async function signTx(
    template: UnsignedTransactionTemplate,
    privateKeyWIF: string,
): Promise<SignResult> {
    return signTransaction(template, privateKeyWIF)
}

// ============================================================================
// Orchestration (Build + Sign + Broadcast)
// ============================================================================

/**
 * Retrieves the server share for a user.
 * Currently uses the stub API - will be replaced with real HTTP call.
 */
async function getServerShare(userId: string, deviceId: string): Promise<string | null> {
    // Import API dynamically to avoid circular dependency
    const { default: api } = await import('../api/api')
    const result = await api.getBsvServerShare(userId, deviceId)
    return result?.treechatShare ?? null
}

/**
 * Broadcasts a signed transaction via the backend.
 * Currently a stub - will be replaced with real HTTP call.
 */
async function broadcastViaBackend(
    signedTxHex: string,
    txType: string,
    metadata: Record<string, unknown>,
): Promise<{ success: boolean; txid?: string; error?: string; retry?: boolean }> {
    // TODO: Replace with real API call
    // POST /api/v1/bsv/broadcast_client_signed_tx
    console.log('>>> [STUB] broadcastViaBackend called:', {
        txHexLength: signedTxHex.length,
        txType,
        metadata,
    })

    // For now, return a mock success
    // In production, this would call the backend which handles:
    // 1. UTXO cache check for double-spend
    // 2. Broadcast via ARC/WoC with retry logic
    // 3. Record creation (BsvTx, upvalue, etc.)
    return {
        success: true,
        txid: 'mock-txid-' + Date.now().toString(16),
    }
}

/**
 * Internal helper: retrieves the share pair, recovers the local wallet,
 * and returns the payment WIF for signing.
 */
async function retrievePaymentKeyFromSelfCustody(
    userId: string,
    expectedAddress: string,
): Promise<{ success: true; wif: string } | { success: false; error: string; retryable: boolean }> {
    // Step 1: Get client share from IndexedDB
    const clientRecord = await getClientShare(userId)
    if (!clientRecord) {
        return {
            success: false,
            error: 'No client share found. Please set up self-custody wallet first.',
            retryable: false,
        }
    }

    // Step 2: Get server share from API
    const serverShare = await getServerShare(userId, clientRecord.deviceId)
    if (!serverShare) {
        return {
            success: false,
            error: 'Could not retrieve server share. Please try again.',
            retryable: true,
        }
    }

    try {
        const wif = await recoverPaymentKeyWifWithShares(
            userId,
            serverShare,
            clientRecord.clientShare,
            expectedAddress,
        )

        return { success: true, wif }
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        return {
            success: false,
            error: errorMessage,
            retryable: false,
        }
    }
}

/**
 * Internal helper: signs and broadcasts a built transaction with retry logic.
 */
async function signAndBroadcastWithRetry(
    userId: string,
    senderAddress: string,
    buildFn: () => Promise<BuildResult>,
    txType: string,
    metadata: Record<string, unknown>,
): Promise<BroadcastResult> {
    let attempts = 0
    let lastError = ''

    while (attempts < MAX_RETRY_ATTEMPTS) {
        attempts++

        try {
            // Get WIF from Shamir shares
            const shareResult = await retrievePaymentKeyFromSelfCustody(userId, senderAddress)
            if (shareResult.success === false) {
                return {
                    success: false,
                    error: shareResult.error,
                    retryable: shareResult.retryable,
                }
            }
            const wif = shareResult.wif

            // Build transaction (fresh UTXOs on each attempt)
            const buildResult = await buildFn()
            if (!buildResult.success || !buildResult.template) {
                return {
                    success: false,
                    error: buildResult.error ?? 'Failed to build transaction',
                    retryable: buildResult.error?.includes('Insufficient funds') ? false : true,
                }
            }

            // Sign transaction
            const signResult = await signTransaction(buildResult.template, wif)

            // Note: In JS we can't truly zeroize memory, but we can let the variable
            // go out of scope. The private key is only in memory briefly.

            if (!signResult.success || !signResult.rawTx) {
                return {
                    success: false,
                    error: signResult.error ?? 'Failed to sign transaction',
                    retryable: false,
                }
            }

            // Broadcast via backend
            const broadcastResult = await broadcastViaBackend(signResult.rawTx, txType, {
                ...metadata,
                fee: buildResult.fee,
            })

            if (broadcastResult.success) {
                return {
                    success: true,
                    txid: broadcastResult.txid,
                    rawTx: signResult.rawTx,
                }
            }

            // Check if we should retry
            if (broadcastResult.retry) {
                lastError = broadcastResult.error ?? 'UTXO conflict'
                console.log(`>>> Attempt ${attempts}/${MAX_RETRY_ATTEMPTS} failed: ${lastError}`)

                if (attempts < MAX_RETRY_ATTEMPTS) {
                    await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS))
                    continue
                }
            }

            return {
                success: false,
                error: broadcastResult.error ?? 'Broadcast failed',
                retryable: false,
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error)
            lastError = errorMessage
            console.error(`>>> Attempt ${attempts}/${MAX_RETRY_ATTEMPTS} error:`, errorMessage)

            if (attempts < MAX_RETRY_ATTEMPTS) {
                await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS))
                continue
            }
        }
    }

    return {
        success: false,
        error: `Failed after ${MAX_RETRY_ATTEMPTS} attempts: ${lastError}`,
        retryable: true,
    }
}

/**
 * Complete flow for a simple payment: build, sign with Shamir shares, broadcast.
 *
 * @param userId - User ID for Shamir share retrieval
 * @param senderAddress - Address to send from
 * @param params - Payment parameters (single recipient)
 * @param metadata - Additional metadata for backend records
 * @returns BroadcastResult with txid on success
 */
export async function buildSignAndBroadcastPayment(
    userId: string,
    senderAddress: string,
    params: PaymentParams,
    metadata: Record<string, unknown> = {},
): Promise<BroadcastResult> {
    return signAndBroadcastWithRetry(
        userId,
        senderAddress,
        () => buildPaymentTransaction(senderAddress, params),
        'payment',
        {
            ...metadata,
            amount: params.amount,
            recipientAddress: params.recipientAddress,
        },
    )
}

/**
 * Complete flow for an upvalue: build, sign with Shamir shares, broadcast.
 * Automatically splits the amount 85% to author, 15% to Treechat.
 *
 * @param userId - User ID for Shamir share retrieval
 * @param senderAddress - Address to send from
 * @param params - Upvalue parameters (author address, Treechat fee address, total amount)
 * @param metadata - Additional metadata for backend records (e.g., answerId)
 * @returns BroadcastResult with txid on success
 */
export async function buildSignAndBroadcastUpvalue(
    userId: string,
    senderAddress: string,
    params: UpvalueParams,
    metadata: Record<string, unknown> = {},
): Promise<BroadcastResult> {
    const treechatAmount = Math.floor(params.totalAmount * TREECHAT_FEE_PERCENTAGE)
    const authorAmount = params.totalAmount - treechatAmount

    return signAndBroadcastWithRetry(
        userId,
        senderAddress,
        () => buildUpvalueTransaction(senderAddress, params),
        'upvalue',
        {
            ...metadata,
            totalAmount: params.totalAmount,
            authorAmount,
            treechatAmount,
            authorAddress: params.authorAddress,
            treechatFeeAddress: params.treechatFeeAddress,
        },
    )
}

// Backwards compatibility alias
export const buildSignAndBroadcast = buildSignAndBroadcastPayment

// ============================================================================
// Testing Utilities (exposed on window.bsvTxBuilder for console testing)
// ============================================================================

/**
 * Test UTXO fetching for an address.
 */
export async function testFetchUtxos(address: string): Promise<void> {
    console.log(`>>> Fetching UTXOs for ${address}...`)
    try {
        const utxos = await fetchUtxos(address)
        console.log(`>>> Found ${utxos.length} UTXOs:`)
        utxos.forEach((u, i) => {
            console.log(
                `>>>   [${i}] ${u.tx_hash}:${u.tx_pos} = ${u.value} sats (height: ${u.height})`,
            )
        })
        const total = utxos.reduce((sum, u) => sum + u.value, 0)
        console.log(`>>> Total: ${total} sats`)
    } catch (error) {
        console.error('>>> Error:', error)
    }
}

// ============================================================================
// Mock Data Generators for Testing
// ============================================================================

/**
 * Creates a mock UTXO for testing.
 */
export function createMockUtxo(value: number, txHash?: string, txPos?: number): SpendableUtxo {
    return {
        tx_hash: txHash ?? 'a'.repeat(64),
        tx_pos: txPos ?? 0,
        value,
        height: 800000,
        scriptPubKey: '76a914' + '0'.repeat(40) + '88ac', // dummy P2PKH script
    }
}

/**
 * Creates multiple mock UTXOs for testing.
 */
export function createMockUtxos(values: number[]): SpendableUtxo[] {
    return values.map((value, i) => createMockUtxo(value, 'a'.repeat(63) + i.toString(16), i))
}

// ============================================================================
// Individual Component Tests
// ============================================================================

/**
 * Test UTXO selection with mock data.
 */
export function testSelectUtxos(utxoValues: number[], requiredAmount: number): void {
    console.log(`>>> Testing UTXO selection:`)
    console.log(`>>>   Available UTXOs: [${utxoValues.join(', ')}] sats`)
    console.log(`>>>   Required amount: ${requiredAmount} sats`)

    const utxos = createMockUtxos(utxoValues)
    const result = selectUtxos(utxos, requiredAmount)

    if (result) {
        console.log(`>>> Selection successful:`)
        console.log(`>>>   Selected ${result.selected.length} UTXOs`)
        console.log(`>>>   Selected values: [${result.selected.map(u => u.value).join(', ')}]`)
        console.log(`>>>   Total selected: ${result.total} sats`)
        console.log(`>>>   Excess: ${result.total - requiredAmount} sats`)
    } else {
        const total = utxoValues.reduce((a, b) => a + b, 0)
        console.log(`>>> Selection failed: insufficient funds`)
        console.log(`>>>   Total available: ${total} sats`)
        console.log(`>>>   Shortfall: ${requiredAmount - total} sats`)
    }
}

/**
 * Test fee estimation.
 */
export function testEstimateFee(
    inputCount: number,
    outputCount: number,
    opReturnSize: number = 0,
): void {
    console.log(`>>> Testing fee estimation:`)
    console.log(`>>>   Inputs: ${inputCount}`)
    console.log(`>>>   Outputs: ${outputCount}`)
    console.log(`>>>   OP_RETURN size: ${opReturnSize} bytes`)

    const size = estimateTxSize(inputCount, outputCount, opReturnSize)
    const fee = calculateFee(size)

    console.log(`>>> Results:`)
    console.log(`>>>   Estimated tx size: ${size} bytes`)
    console.log(`>>>   Fee (1 sat/byte): ${fee} sats`)
}

/**
 * Test P2PKH script generation.
 */
export function testGenerateScript(address: string): void {
    console.log(`>>> Generating P2PKH script for: ${address}`)
    try {
        const script = generateP2PKHScript(address)
        console.log(`>>> Script (hex): ${script}`)
        console.log(`>>>   Length: ${script.length / 2} bytes`)
    } catch (error) {
        console.error(`>>> Error:`, error)
    }
}

/**
 * Test transaction assembly with mock UTXOs (no network calls).
 */
export function testAssembleTransaction(
    utxoValues: number[],
    outputs: Array<{ address: string; satoshis: number }>,
    changeAddress: string = '1ChangeAddress11111111111111111114oLvT2',
): BuildResult {
    console.log(`>>> Testing transaction assembly:`)
    console.log(
        `>>>   UTXOs: [${utxoValues.join(', ')}] sats (total: ${utxoValues.reduce(
            (a, b) => a + b,
            0,
        )})`,
    )
    console.log(`>>>   Outputs:`)
    outputs.forEach((o, i) => {
        console.log(`>>>     [${i}] ${o.address.slice(0, 20)}... = ${o.satoshis} sats`)
    })
    console.log(`>>>   Change to: ${changeAddress.slice(0, 20)}...`)

    const utxos = createMockUtxos(utxoValues)
    const result = assembleTransaction(utxos, changeAddress, outputs)

    if (result.success) {
        console.log(`>>> Assembly successful:`)
        console.log(`>>>   Inputs: ${result.template?.inputs.length}`)
        console.log(`>>>   Outputs: ${result.template?.outputs.length}`)
        console.log(`>>>   Total input: ${result.totalInput} sats`)
        console.log(`>>>   Total output: ${result.totalOutput} sats`)
        console.log(`>>>   Fee: ${result.fee} sats`)

        // Show output breakdown
        console.log(`>>>   Output breakdown:`)
        result.template?.outputs.forEach((o, i) => {
            if (o.satoshis === 0) {
                console.log(`>>>     [${i}] OP_RETURN`)
            } else {
                console.log(`>>>     [${i}] ${o.satoshis} sats`)
            }
        })
    } else {
        console.error(`>>> Assembly failed: ${result.error}`)
    }

    return result
}

/**
 * Test upvalue split calculation.
 */
export function testUpvalueSplit(totalAmount: number): void {
    const treechatAmount = Math.floor(totalAmount * TREECHAT_FEE_PERCENTAGE)
    const authorAmount = totalAmount - treechatAmount

    console.log(`>>> Upvalue split for ${totalAmount} sats:`)
    console.log(`>>>   Author (85%): ${authorAmount} sats`)
    console.log(`>>>   Treechat (15%): ${treechatAmount} sats`)
    console.log(`>>>   Sum: ${authorAmount + treechatAmount} sats`)

    if (authorAmount < DUST_THRESHOLD) {
        console.warn(`>>>   ⚠️ Author amount below dust threshold (${DUST_THRESHOLD})`)
    }
    if (treechatAmount < DUST_THRESHOLD) {
        console.warn(`>>>   ⚠️ Treechat amount below dust threshold (${DUST_THRESHOLD})`)
    }
}

// ============================================================================
// Higher-Level Tests (require network or real addresses)
// ============================================================================

/**
 * Test payment transaction building with real UTXO fetch.
 */
export async function testBuildPaymentLive(
    senderAddress: string,
    recipientAddress: string,
    amount: number,
): Promise<BuildResult> {
    console.log(`>>> Building PAYMENT transaction (live UTXO fetch):`)
    console.log(`>>>   From: ${senderAddress}`)
    console.log(`>>>   To: ${recipientAddress}`)
    console.log(`>>>   Amount: ${amount} sats`)

    const result = await buildPaymentTransaction(senderAddress, {
        recipientAddress,
        amount,
    })

    if (result.success) {
        console.log(`>>> Build successful:`)
        console.log(`>>>   Inputs: ${result.template?.inputs.length}`)
        console.log(`>>>   Outputs: ${result.template?.outputs.length}`)
        console.log(`>>>   Total input: ${result.totalInput} sats`)
        console.log(`>>>   Total output: ${result.totalOutput} sats`)
        console.log(`>>>   Fee: ${result.fee} sats`)
    } else {
        console.error(`>>> Build failed: ${result.error}`)
    }

    return result
}

/**
 * Test upvalue transaction building with real UTXO fetch.
 */
export async function testBuildUpvalueLive(
    senderAddress: string,
    authorAddress: string,
    treechatFeeAddress: string,
    totalAmount: number,
): Promise<BuildResult> {
    const treechatAmount = Math.floor(totalAmount * TREECHAT_FEE_PERCENTAGE)
    const authorAmount = totalAmount - treechatAmount

    console.log(`>>> Building UPVALUE transaction (live UTXO fetch):`)
    console.log(`>>>   From: ${senderAddress}`)
    console.log(`>>>   To author (85%): ${authorAddress} = ${authorAmount} sats`)
    console.log(`>>>   To Treechat (15%): ${treechatFeeAddress} = ${treechatAmount} sats`)
    console.log(`>>>   Total: ${totalAmount} sats`)

    const result = await buildUpvalueTransaction(senderAddress, {
        authorAddress,
        treechatFeeAddress,
        totalAmount,
    })

    if (result.success) {
        console.log(`>>> Build successful:`)
        console.log(`>>>   Inputs: ${result.template?.inputs.length}`)
        console.log(`>>>   Outputs: ${result.template?.outputs.length}`)
        console.log(`>>>   Total input: ${result.totalInput} sats`)
        console.log(`>>>   Total output: ${result.totalOutput} sats`)
        console.log(`>>>   Fee: ${result.fee} sats`)
    } else {
        console.error(`>>> Build failed: ${result.error}`)
    }

    return result
}

// Backwards compatibility aliases
export const testBuildPayment = testBuildPaymentLive
export const testBuildUpvalue = testBuildUpvalueLive
export const testBuildTx = testBuildPaymentLive

/**
 * Show help for console testing.
 */
export function help(): void {
    console.log(`
=== BSV Transaction Builder ===

STEP-BY-STEP WORKFLOW:

  // 1. Get fee rate from backend (cached 15m)
  feeRate = await bsvTxBuilder.fetchFeeRate()

  // 2. Fetch UTXOs (includes scriptPubKey)
  utxos = await bsvTxBuilder.fetchUtxos('1YourAddress...')

  // 3. Build unsigned tx (pass feeRate)
  tx = bsvTxBuilder.assembleTransaction(
    utxos,
    '1YourAddress...',  // change address
    [{ address: '1Recipient...', satoshis: 1000 }],
    undefined,          // opReturnData
    feeRate             // fee rate from step 1
  )
  // → { success, template, fee, totalInput, totalOutput }

  // 4. Sign with private key
  signed = await bsvTxBuilder.signTx(tx.template, 'L1yourWIF...')
  // → { success, rawTx }

  // 5. Broadcast (TODO - send signed.rawTx to backend)

FUNCTIONS:

  fetchFeeRate() → Promise<number>  // sat/byte from backend
  fetchUtxos(address) → Promise<SpendableUtxo[]>
  selectUtxos(utxos, requiredSats) → { selected, total } | null
  assembleTransaction(utxos, changeAddr, outputs, opReturn?, feeRate?) → BuildResult
  signTx(template, wif) → Promise<{ success, rawTx }>
  generateP2PKHScript(address) → string
  estimateTxSize(inputs, outputs) → number
  calculateFee(bytes, feeRate?) → number
`)
}
